// The studio's Firebase Auth session (TODO §9.1). SlideCraft owns the login for the whole
// studio; the Gridd Library (§8.3) reuses this session rather than running its own — the
// signed-in user's ID token is forwarded into the library iframe as a bearer token.
//
// The web config is injected at build time (see webpack.config.js / `src/lib/config.ts`)
// and points at the SAME Firebase project as the DeckAI-AddIn (`gridd-cb9b6`), so
// identities match across the studio, the library, and the add-in.
import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";

import { FIREBASE_CONFIG } from "@/lib/config";

const app: FirebaseApp = initializeApp(FIREBASE_CONFIG);
// `getAuth` uses browser-local persistence + the popup/redirect resolver by default, so the
// session survives reloads (§9.7.2) and `signInWithPopup` works with no extra wiring.
const auth: Auth = getAuth(app);

export { auth };

// The serializable projection of a Firebase user we surface to the UI (§9.1.2). The live
// `User` object (which owns `getIdToken`) is never stored — it's read from `auth.currentUser`
// at call time so tokens are always minted against the current identity.
export type SessionUser = {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
};

export function toSessionUser(user: User | null): SessionUser | null {
  if (!user) return null;
  const { uid, displayName, email, photoURL } = user;
  return { uid, displayName, email, photoURL };
}

// §9.1.3 Subscribe to sign-in/out. Returns the unsubscribe fn for effect cleanup.
export function subscribeToAuth(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, cb);
}

// §9.2.1 Browser sign-in. All three providers the Firebase project enables, matched to the
// DeckAI-AddIn so accounts are shared. Popup (not redirect/Office SSO) is the browser path.
// These reject on failure — the caller (AuthProvider) maps errors to toasts (§9.2.2).
export function signInWithGoogle(): Promise<User> {
  return signInWithPopup(auth, new GoogleAuthProvider()).then((r) => r.user);
}

// Microsoft = the generic OIDC `OAuthProvider("microsoft.com")`, same id the add-in uses
// (default `common` tenant — personal + work/school accounts).
export function signInWithMicrosoft(): Promise<User> {
  return signInWithPopup(auth, new OAuthProvider("microsoft.com")).then(
    (r) => r.user,
  );
}

export function signInWithEmailPassword(
  email: string,
  password: string,
): Promise<User> {
  return signInWithEmailAndPassword(auth, email, password).then((r) => r.user);
}

export function signOutUser(): Promise<void> {
  return signOut(auth);
}

// The current user's Firebase ID token, or `null` when signed out. This is the seam the
// Gridd Library calls (§8.3.1/§8.3.3): it reads the LIVE `auth.currentUser`, so it always
// mints against the current identity, and `forceRefresh` re-mints an expired token (§8.3.3)
// rather than ever returning a stale one. A mint failure degrades to a null token.
//
// §14.6.4 REGRESSION NOTE — a refresh failure must never sign the user out. Offline (the
// whole point of the desktop app) this path fails every time after ~1h, and the natural
// "fix" for a null token is to clear the session. Doing that would log a user out mid-flight,
// on a machine that is holding a perfectly valid cached session, with no way to sign back in.
// The session stands; only the bearer is missing. `AuthContextValue.tokenFresh` is how
// consumers tell the two apart.
export async function getFreshIdToken(
  forceRefresh = false,
): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken(forceRefresh);
  } catch {
    return null;
  }
}
